(() => {
  const elementName = 'fridge-slide-finder';

  if (customElements.get(elementName)) return;

  const slideOrder = ['small', 'medium', 'large'];
  const normalizeSearch = (value) =>
    String(value || '')
      .toLocaleLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  class FridgeSlideFinder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      try {
        this.allModels = this.readJson('[data-compatibility-data]');
        this.products = this.readJson('[data-product-data]');
      } catch (error) {
        this.showDataError(error);
        return;
      }

      this.showLegacy = this.dataset.showLegacy !== 'false';
      this.currentSlide = this.dataset.currentSlide || '';
      this.models = this.showLegacy
        ? this.allModels
        : this.allModels.filter((model) => model.market !== 'Legacy');

      this.brandSelect = this.querySelector('[data-brand-select]');
      this.modelSelect = this.querySelector('[data-model-select]');
      this.resetButton = this.querySelector('[data-reset]');
      this.selectionHint = this.querySelector('[data-selection-hint]');
      this.result = this.querySelector('[data-result]');
      this.resultTitle = this.querySelector('[data-result-title]');
      this.resultMeta = this.querySelector('[data-result-meta]');
      this.resultAnnouncement = this.querySelector('[data-result-announcement]');
      this.matrixBody = this.querySelector('[data-matrix-body]');
      this.matrixSearch = this.querySelector('[data-matrix-search]');
      this.includeLegacy = this.querySelector('[data-include-legacy]');
      this.matrixCount = this.querySelector('[data-matrix-count]');

      this.populateBrands();
      this.bindEvents();
      this.renderMatrix();
    }

    readJson(selector) {
      const node = this.querySelector(selector);
      if (!node) throw new Error(`Missing JSON payload: ${selector}`);
      return JSON.parse(node.textContent);
    }

    showDataError(error) {
      const hint = this.querySelector('[data-selection-hint]');
      if (hint) hint.textContent = 'Compatibility data is temporarily unavailable.';
      console.error('Fridge slide finder could not start.', error);
    }

    bindEvents() {
      this.brandSelect?.addEventListener('change', () => this.handleBrandChange());
      this.modelSelect?.addEventListener('change', () => this.handleModelChange());
      this.resetButton?.addEventListener('click', () => this.resetFinder());
      this.matrixSearch?.addEventListener('input', () => this.renderMatrix());
      this.includeLegacy?.addEventListener('change', () => this.renderMatrix());
    }

    populateBrands() {
      if (!this.brandSelect) return;

      const brands = [...new Set(this.models.map((model) => model.brand))];
      const fragment = document.createDocumentFragment();

      brands.forEach((brand) => {
        const option = document.createElement('option');
        option.value = brand;
        option.textContent = brand;
        fragment.appendChild(option);
      });

      this.brandSelect.appendChild(fragment);
    }

    handleBrandChange() {
      const brand = this.brandSelect.value;
      this.populateModels(brand);
      this.hideResult();
      this.resetButton.disabled = !brand;
      this.selectionHint.textContent = brand
        ? `Now choose your ${brand} fridge model.`
        : 'Select a brand to see its compatible fridge models.';
    }

    populateModels(brand) {
      this.modelSelect.replaceChildren();

      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = 'Choose a model';
      this.modelSelect.appendChild(placeholder);

      if (!brand) {
        this.modelSelect.disabled = true;
        return;
      }

      const fragment = document.createDocumentFragment();
      this.models.forEach((model, index) => {
        if (model.brand !== brand) return;

        const option = document.createElement('option');
        option.value = String(index);
        option.textContent = [
          model.model,
          model.capacity ? `(${model.capacity})` : '',
          model.market === 'Legacy' ? '— Legacy' : '',
        ]
          .filter(Boolean)
          .join(' ');
        fragment.appendChild(option);
      });

      this.modelSelect.appendChild(fragment);
      this.modelSelect.disabled = false;
    }

    handleModelChange() {
      if (!this.modelSelect.value) {
        this.hideResult();
        this.selectionHint.textContent = `Choose your ${this.brandSelect.value} fridge model.`;
        return;
      }

      const model = this.models[Number(this.modelSelect.value)];
      if (!model) return;

      this.renderResult(model);
      this.selectionHint.textContent = `Showing compatibility for ${model.brand} ${model.model}.`;
      this.resetButton.disabled = false;
    }

    renderResult(model) {
      const recommendedSlide = slideOrder.find((slide) => model.slides[slide]);
      const recommendedProduct = this.products[recommendedSlide];
      const meta = [];

      if (model.capacity) meta.push(model.capacity);
      if (model.dimensions?.length && model.dimensions?.width) {
        meta.push(`${model.dimensions.length} × ${model.dimensions.width} mm footprint`);
      }
      if (model.market === 'Legacy') meta.push('Legacy model');

      this.resultTitle.textContent = `${model.brand} ${model.model}`;
      this.resultMeta.textContent = meta.join(' · ');
      this.querySelectorAll('[data-slide-card]').forEach((card) => {
        const slide = card.dataset.slideCard;
        const fit = model.slides[slide];
        const product = this.products[slide];
        const status = card.querySelector('[data-status]');
        const note = card.querySelector('[data-note]');
        const link = card.querySelector('[data-product-link]');
        const imageWrap = card.querySelector('[data-product-image-wrap]');
        const image = card.querySelector('[data-product-image]');
        const currentLabel = card.querySelector('[data-current-label]');

        card.classList.toggle('is-fit', fit?.status === 'fit');
        card.classList.toggle('is-caution', fit?.status === 'caution');
        card.classList.toggle('is-unlisted', !fit);
        card.classList.toggle('is-recommended', slide === recommendedSlide);
        card.classList.toggle('is-current', slide === this.currentSlide);

        status.textContent = fit?.label || 'Not compatible';
        note.textContent =
          fit?.note || 'This fridge model is not compatible with this slide size.';
        currentLabel.hidden = slide !== this.currentSlide;
        this.configureImage(imageWrap, image, product);

        if (fit) {
          this.configureLink(link, product, `View ${product.label}`);
        } else {
          link.hidden = true;
          link.removeAttribute('href');
        }
      });

      this.result.hidden = false;
      this.resultAnnouncement.textContent =
        `${model.brand} ${model.model}. ${recommendedProduct.label} is the recommended compatible option.`;
    }

    configureImage(imageWrap, image, product) {
      if (!imageWrap || !image) return;

      if (!product?.image) {
        imageWrap.hidden = true;
        image.removeAttribute('src');
        image.removeAttribute('width');
        image.removeAttribute('height');
        return;
      }

      image.src = product.image;
      image.alt = product.imageAlt || product.label || '';
      if (product.imageWidth) image.width = product.imageWidth;
      if (product.imageHeight) image.height = product.imageHeight;
      imageWrap.hidden = false;
    }

    configureLink(link, product, label) {
      if (!link || !product?.configured || !product.url) {
        if (link) link.hidden = true;
        return;
      }

      link.href = product.url;
      link.textContent = [
        label,
        product.available ? '' : 'Sold out',
      ]
        .filter(Boolean)
        .join(' · ');
      link.setAttribute('aria-label', `${label}${product.available ? '' : ', sold out'}`);
      link.hidden = false;
    }

    hideResult() {
      if (this.result) this.result.hidden = true;
      if (this.resultAnnouncement) this.resultAnnouncement.textContent = '';
    }

    resetFinder() {
      this.brandSelect.value = '';
      this.populateModels('');
      this.hideResult();
      this.resetButton.disabled = true;
      this.selectionHint.textContent = 'Select a brand to see its compatible fridge models.';
      this.brandSelect.focus();
    }

    renderMatrix() {
      if (!this.matrixBody) return;

      const query = normalizeSearch(this.matrixSearch?.value);
      const includeLegacy = this.includeLegacy ? this.includeLegacy.checked : this.showLegacy;
      const filteredModels = this.allModels.filter((model) => {
        if (!this.showLegacy && model.market === 'Legacy') return false;
        if (!includeLegacy && model.market === 'Legacy') return false;
        if (!query) return true;

        const haystack = normalizeSearch(
          [model.brand, model.model, model.capacity, model.market].filter(Boolean).join(' '),
        );
        return haystack.includes(query);
      });

      const fragment = document.createDocumentFragment();
      filteredModels.forEach((model) => fragment.appendChild(this.createMatrixRow(model)));

      if (!filteredModels.length) {
        const row = document.createElement('tr');
        const cell = document.createElement('td');
        cell.colSpan = 7;
        cell.textContent = 'No fridge models match your search.';
        row.appendChild(cell);
        fragment.appendChild(row);
      }

      this.matrixBody.replaceChildren(fragment);

      if (this.matrixCount) {
        const availableTotal = this.showLegacy
          ? this.allModels.length
          : this.allModels.filter((model) => model.market !== 'Legacy').length;
        this.matrixCount.textContent = `Showing ${filteredModels.length} of ${availableTotal} models`;
      }
    }

    createMatrixRow(model) {
      const row = document.createElement('tr');
      row.classList.toggle('is-legacy', model.market === 'Legacy');

      row.appendChild(this.createTextCell(model.brand));

      const modelCell = document.createElement('td');
      modelCell.className = 'ka-fsf__model-cell';
      const modelName = document.createElement('strong');
      modelName.textContent = model.model;
      modelCell.appendChild(modelName);
      if (model.market === 'Legacy') {
        const legacy = document.createElement('span');
        legacy.className = 'ka-fsf__legacy-label';
        legacy.textContent = 'Legacy model';
        modelCell.appendChild(legacy);
      }
      row.appendChild(modelCell);

      row.appendChild(this.createTextCell(model.capacity || '—'));
      row.appendChild(
        this.createTextCell(
          model.dimensions?.length && model.dimensions?.width
            ? `${model.dimensions.length} × ${model.dimensions.width} mm`
            : 'Not published',
        ),
      );

      slideOrder.forEach((slide) => {
        const cell = document.createElement('td');
        const fit = model.slides[slide];
        const status = document.createElement('span');
        status.className = [
          'ka-fsf__matrix-status',
          fit ? `is-${fit.status}` : 'is-unlisted',
        ].join(' ');
        status.textContent = fit?.label || 'Not compatible';
        if (fit?.note) {
          status.title = fit.note;
          status.setAttribute('aria-label', `${fit.label}. ${fit.note}`);
        }
        cell.appendChild(status);
        row.appendChild(cell);
      });

      return row;
    }

    createTextCell(value) {
      const cell = document.createElement('td');
      cell.textContent = value;
      return cell;
    }
  }

  customElements.define(elementName, FridgeSlideFinder);
})();
